import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [workspaceUnread, setWorkspaceUnread] = useState({});
  const [projectUnread, setProjectUnread] = useState({});

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const [countRes, byWorkspaceRes] = await Promise.all([
        api.get('/chat/unread-count'),
        api.get('/chat/unread-by-workspace')
      ]);
      setUnreadCount(countRes.data.count || 0);
      setWorkspaceUnread(byWorkspaceRes.data.workspace_unread || {});
      setProjectUnread(byWorkspaceRes.data.project_unread || {});
    } catch (error) {
      // Silently fail
    }
  }, [isAuthenticated]);

  // Mark conversation as read and immediately update counts
  const markConversationRead = useCallback(async (conversationId) => {
    if (!isAuthenticated) return;
    
    try {
      await api.post(`/chat/mark-read/${conversationId}`);
      // Immediately refresh counts
      await refreshUnreadCount();
    } catch (error) {
      // Silently fail
    }
  }, [isAuthenticated, refreshUnreadCount]);

  // Initial fetch and periodic refresh - only when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setWorkspaceUnread({});
      setProjectUnread({});
      return;
    }
    
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  return (
    <ChatContext.Provider value={{
      unreadCount,
      workspaceUnread,
      projectUnread,
      refreshUnreadCount,
      markConversationRead
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;
